import React, { useState, useEffect, useCallback } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useActiveTenant } from "../hooks/useActiveTenant";
import NoTenantSelected from "../components/NoTenantSelected";
import {
  fetchSemesters,
  createSemester,
  deleteSemester,
} from "../api/apiClient";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventIcon from "@mui/icons-material/Event";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import VisibilityIcon from "@mui/icons-material/Visibility";

function makeStatusConfig(tSecondary, tAccent) {
  return {
    DRAFT: {
      label: "Draft",
      accentColor: tAccent,
      badgeBg: alpha(tAccent, 0.10),
      badgeColor: tAccent,
    },
    PUBLISHED: {
      label: "Published",
      accentColor: tSecondary,
      badgeBg: alpha(tSecondary, 0.10),
      badgeColor: tSecondary,
    },
    ARCHIVED: {
      label: "Archived",
      accentColor: "var(--color-ink-4)",
      badgeBg: "var(--color-surface-press-light)",
      badgeColor: "var(--color-ink-3)",
    },
  };
}

function StatusBadge({ status }) {
  const theme = useTheme();
  const STATUS_CONFIG = makeStatusConfig(theme.palette.secondary.main, theme.palette.warning.main);
  const cfg = STATUS_CONFIG[(status || "").toUpperCase()] || STATUS_CONFIG.ARCHIVED;
  return (
    <Box
      sx={{
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: ".05em",
        textTransform: "uppercase",
        px: "10px",
        py: "3px",
        borderRadius: "999px",
        background: cfg.badgeBg,
        color: cfg.badgeColor,
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        flexShrink: 0,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.badgeColor,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </Box>
  );
}

function GroupSection({ label, count, children }) {
  return (
    <Box sx={{ mb: "30px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          mb: "14px",
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 800,
            color: "var(--color-ink-2)",
            textTransform: "uppercase",
            letterSpacing: ".06em",
            m: 0,
            flexShrink: 0,
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            fontSize: "11.5px",
            fontWeight: 700,
            px: "9px",
            py: "2px",
            borderRadius: "999px",
            background: "var(--color-surface-press-light)",
            color: "var(--color-ink-3)",
            flexShrink: 0,
          }}
        >
          {count}
        </Box>
        <Box sx={{ flex: 1, height: "1px", background: "var(--color-hairline-cloud)" }} />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
          gap: "14px",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function SemesterCard({ semester, onOpen, onDelete }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDk = theme.palette.primary.dark;
  const tSecondary = theme.palette.secondary.main;
  const STATUS_CONFIG = makeStatusConfig(tSecondary, theme.palette.warning.main);
  const status = (semester.status || "").toUpperCase();
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.ARCHIVED;
  const isDraft = status === "DRAFT";
  const isPublished = status === "PUBLISHED";
  const isArchived = status === "ARCHIVED";

  const formattedDates = [semester.startDate, semester.endDate]
    .filter(Boolean)
    .join(" — ");

  return (
    <Box
      onClick={onOpen}
      sx={{
        background: "#fff",
        border: "1px solid var(--color-hairline-cloud)",
        borderRadius: "16px",
        boxShadow: "0 1px 3px rgba(20,24,40,.05)",
        overflow: "hidden",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        transition: "border-color .14s, box-shadow .14s, transform .14s",
        "&:hover": {
          borderColor: "#D6D2C8",
          boxShadow: "0 8px 24px -10px rgba(20,24,40,.14)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Status accent bar */}
      <Box sx={{ height: "4px", background: cfg.accentColor, flexShrink: 0 }} />

      {/* Card body */}
      <Box
        sx={{
          p: "18px 20px 20px",
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          flex: 1,
        }}
      >
        {/* Top: name + status badge */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 800,
                letterSpacing: "-.02em",
                color: "var(--color-ink)",
                lineHeight: 1.25,
                fontFamily: "var(--font-display)",
              }}
            >
              {semester.name}
            </Typography>
            {semester.description && (
              <Typography
                sx={{ fontSize: "12.5px", color: "var(--color-ink-3)", mt: "2px" }}
              >
                {semester.description}
              </Typography>
            )}
          </Box>
          <StatusBadge status={semester.status} />
        </Box>

        {/* Meta: date range + class count */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "9px",
            py: "14px",
            borderTop: "1px solid var(--color-surface-press-light)",
            borderBottom: "1px solid var(--color-surface-press-light)",
          }}
        >
          {formattedDates && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <EventIcon sx={{ fontSize: 15, color: "var(--color-ink-4)", flexShrink: 0 }} />
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: "var(--color-ink-2)",
                }}
              >
                {formattedDates}
              </Typography>
            </Box>
          )}
          {semester.classCount != null && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <SchoolIcon sx={{ fontSize: 15, color: "var(--color-ink-4)", flexShrink: 0 }} />
              <Typography
                sx={{ fontSize: "13px", fontWeight: 500, color: "var(--color-ink-2)" }}
              >
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: "var(--color-ink)" }}
                >
                  {semester.classCount}
                </Box>
                {" classes"}
                {semester.enrolledCount != null &&
                  ` · ${semester.enrolledCount} enrolled`}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer: status-aware action buttons */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "auto" }}
        >
          {isDraft && (
            <>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen();
                }}
                startIcon={<CalendarViewMonthIcon sx={{ fontSize: "13px !important" }} />}
                sx={{
                  flex: 1,
                  fontSize: "13px",
                  fontWeight: 700,
                  height: 36,
                  borderRadius: "9px",
                  background: tPrimary,
                  color: "#fff",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": { background: tPrimaryDk, boxShadow: "none" },
                }}
              >
                Open Planning Board
              </Button>
              <Tooltip title="Delete draft">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  sx={{
                    width: 34,
                    height: 34,
                    border: "1.5px solid var(--color-hairline-cloud)",
                    borderRadius: "9px",
                    color: "var(--color-ink-3)",
                    background: "#fff",
                    flexShrink: 0,
                    "&:hover": {
                      background: "rgba(180,35,24,.08)",
                      color: "#B42318",
                      borderColor: "rgba(180,35,24,.18)",
                    },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          )}

          {isPublished && (
            <>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen();
                }}
                startIcon={<VisibilityIcon sx={{ fontSize: "13px !important" }} />}
                sx={{
                  flex: 1,
                  fontSize: "13px",
                  fontWeight: 700,
                  height: 36,
                  borderRadius: "9px",
                  border: "1.5px solid var(--color-hairline-cloud)",
                  color: "var(--color-ink-2)",
                  background: "#fff",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    background: "var(--color-surface-press-light)",
                    borderColor: "var(--color-hairline-cool)",
                    color: "var(--color-ink)",
                    boxShadow: "none",
                  },
                }}
              >
                View Schedule
              </Button>
              {semester.publishDate && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "12px",
                    color: "var(--color-ink-3)",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 13, color: tSecondary }} />
                  {semester.publishDate}
                </Box>
              )}
            </>
          )}

          {isArchived && (
            <>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen();
                }}
                startIcon={<VisibilityIcon sx={{ fontSize: "13px !important" }} />}
                sx={{
                  flex: 1,
                  fontSize: "13px",
                  fontWeight: 700,
                  height: 36,
                  borderRadius: "9px",
                  border: "1.5px solid var(--color-hairline-cloud)",
                  color: "var(--color-ink-2)",
                  background: "#fff",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    background: "var(--color-surface-press-light)",
                    borderColor: "var(--color-hairline-cool)",
                    color: "var(--color-ink)",
                    boxShadow: "none",
                  },
                }}
              >
                View Schedule
              </Button>
              <Tooltip title="Duplicate as new draft (coming soon)">
                <IconButton
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    width: 34,
                    height: 34,
                    border: "1.5px solid var(--color-hairline-cloud)",
                    borderRadius: "9px",
                    color: "var(--color-ink-3)",
                    background: "#fff",
                    flexShrink: 0,
                    "&:hover": {
                      background: "var(--color-surface-press-light)",
                      color: "var(--color-ink)",
                      borderColor: "var(--color-hairline-cool)",
                    },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function SemesterListPage() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDk = theme.palette.primary.dark;
  const navigate = useNavigate();
  const { tenantId: selectedTenantId } = useActiveTenant();
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState("");
  const [form, setForm] = useState({ name: "", startDate: "", endDate: "" });
  const [creating, setCreating] = useState(false);

  const loadSemesters = useCallback(() => {
    if (!selectedTenantId) {
      setSemesters([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchSemesters(selectedTenantId)
      .then((data) => {
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.content)
            ? data.content
            : data?.semesters || [];
        setSemesters(items);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load semesters.");
        setLoading(false);
      });
  }, [selectedTenantId]);

  useEffect(() => {
    loadSemesters();
  }, [loadSemesters]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.startDate || !form.endDate) return;
    if (!selectedTenantId) {
      setCreateError("Select a training center before creating a semester.");
      return;
    }
    setCreateError("");
    setCreating(true);
    try {
      await createSemester(form, selectedTenantId);
      setCreateOpen(false);
      setForm({ name: "", startDate: "", endDate: "" });
      loadSemesters();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Failed to create semester.";
      setCreateError(typeof msg === "string" ? msg : "Failed to create semester.");
      console.error("createSemester error:", err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSemester(id, selectedTenantId);
      loadSemesters();
    } catch {
      setError(
        "Failed to delete semester. Only draft semesters can be deleted.",
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedTenantId) {
    return (
      <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, md: 4.5 }, py: 3.5 }}>
        <NoTenantSelected message="Select a training center from the top bar before planning semesters." />
      </Box>
    );
  }

  const normStatus = (s) => (s.status || "").toUpperCase();
  const activeSemesters = semesters.filter(
    (s) => normStatus(s) === "DRAFT" || normStatus(s) === "PUBLISHED",
  );
  const pastSemesters = semesters.filter((s) => normStatus(s) === "ARCHIVED");
  // catch-all: semesters with unrecognised status fall into active group
  const ungrouped = semesters.filter(
    (s) => !["DRAFT", "PUBLISHED", "ARCHIVED"].includes(normStatus(s)),
  );
  const displayActive = [...activeSemesters, ...ungrouped];

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: "auto",
        px: { xs: 2, md: 4.5 },
        py: 3.5,
        pb: 12,
      }}
    >
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "20px",
          mb: "24px",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: 24, md: 30 },
              fontWeight: 800,
              letterSpacing: "-.035em",
              color: "var(--color-ink)",
              lineHeight: 1.1,
              fontFamily: "var(--font-display)",
            }}
          >
            Semester Planning
          </Typography>
          <Typography sx={{ mt: "6px", fontSize: "14.5px", color: "var(--color-ink-3)" }}>
            Organize training into time blocks — drag templates onto a calendar,
            then publish for students.
          </Typography>
        </Box>
        <Button
          onClick={() => { setCreateError(""); setCreateOpen(true); }}
          startIcon={<AddIcon />}
          sx={{
            height: 42,
            px: "18px",
            borderRadius: "11px",
            fontSize: "13.5px",
            fontWeight: 700,
            background: tPrimary,
            color: "#fff",
            textTransform: "none",
            flexShrink: 0,
            boxShadow: `0 4px 12px -4px ${alpha(tPrimary, 0.45)}`,
            "&:hover": { background: tPrimaryDk, boxShadow: `0 4px 12px -4px ${alpha(tPrimary, 0.45)}` },
          }}
        >
          Create Semester
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {semesters.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 4,
            background: "#fff",
            borderRadius: "16px",
            border: "1px solid var(--color-hairline-cloud)",
          }}
        >
          <CalendarMonthIcon sx={{ fontSize: 64, color: "var(--color-ink-4)", mb: 2 }} />
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 700,
              color: "var(--color-ink-2)",
              mb: 1,
              fontFamily: "var(--font-display)",
            }}
          >
            No Semesters Yet
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "var(--color-ink-3)", mb: 3 }}>
            Create your first semester to start planning training classes.
          </Typography>
          <Button
            onClick={() => { setCreateError(""); setCreateOpen(true); }}
            startIcon={<AddIcon />}
            sx={{
              height: 42,
              px: "18px",
              borderRadius: "11px",
              fontSize: "13.5px",
              fontWeight: 700,
              background: tPrimary,
              color: "#fff",
              textTransform: "none",
              "&:hover": { background: tPrimaryDk },
            }}
          >
            Create Semester
          </Button>
        </Box>
      ) : (
        <>
          {displayActive.length > 0 && (
            <GroupSection
              label="Active & Upcoming"
              count={displayActive.length}
            >
              {displayActive.map((sem) => (
                <SemesterCard
                  key={sem.id}
                  semester={sem}
                  onOpen={() => navigate(`/semesters/${sem.id}`)}
                  onDelete={() => handleDelete(sem.id)}
                />
              ))}
            </GroupSection>
          )}
          {pastSemesters.length > 0 && (
            <GroupSection label="Past Semesters" count={pastSemesters.length}>
              {pastSemesters.map((sem) => (
                <SemesterCard
                  key={sem.id}
                  semester={sem}
                  onOpen={() => navigate(`/semesters/${sem.id}`)}
                  onDelete={() => handleDelete(sem.id)}
                />
              ))}
            </GroupSection>
          )}
        </>
      )}

      {/* Create Semester Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
      >
        {/* Dialog header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "13px",
            p: "22px 24px 18px",
            borderBottom: "1px solid var(--color-surface-press-light)",
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "12px",
              background: tPrimary,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <CalendarViewMonthIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 800,
                letterSpacing: "-.02em",
                fontFamily: "var(--font-display)",
              }}
            >
              Create Semester
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "var(--color-ink-3)", mt: "3px" }}>
              Name your training block and set its date range.
            </Typography>
          </Box>
        </Box>

        <DialogContent
          sx={{
            p: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {createError && (
            <Alert severity="error" onClose={() => setCreateError("")}>
              {createError}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
            <Typography
              sx={{
                fontSize: "11.5px",
                fontWeight: 700,
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "var(--color-ink-3)",
              }}
            >
              Semester Name
            </Typography>
            <TextField
              fullWidth
              value={form.name}
              onChange={(e) =>
                setForm((f) => ({ ...f, name: e.target.value }))
              }
              placeholder="e.g. Spring 2027"
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "11px",
                  background: "var(--color-surface-2)",
                  fontSize: "14px",
                },
              }}
            />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "7px" }}
              >
                <Typography
                  sx={{
                    fontSize: "11.5px",
                    fontWeight: 700,
                    letterSpacing: ".07em",
                    textTransform: "uppercase",
                    color: "var(--color-ink-3)",
                  }}
                >
                  Start Date
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  value={form.startDate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, startDate: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "11px",
                      background: "var(--color-surface-2)",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "7px" }}
              >
                <Typography
                  sx={{
                    fontSize: "11.5px",
                    fontWeight: 700,
                    letterSpacing: ".07em",
                    textTransform: "uppercase",
                    color: "var(--color-ink-3)",
                  }}
                >
                  End Date
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  value={form.endDate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, endDate: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "11px",
                      background: "var(--color-surface-2)",
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: "24px",
            py: "16px",
            background: "var(--color-surface-2)",
            borderTop: "1px solid var(--color-surface-press-light)",
            gap: "9px",
          }}
        >
          <Button
            onClick={() => setCreateOpen(false)}
            sx={{
              height: 34,
              px: "13px",
              borderRadius: "9px",
              fontSize: "13px",
              fontWeight: 700,
              border: "1.5px solid var(--color-hairline-cloud)",
              color: "var(--color-ink-3)",
              textTransform: "none",
              "&:hover": { background: "var(--color-surface-press-light)", color: "var(--color-ink)" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={
              creating || !form.name.trim() || !form.startDate || !form.endDate
            }
            startIcon={<AddIcon sx={{ fontSize: "13px !important" }} />}
            sx={{
              height: 34,
              px: "13px",
              borderRadius: "9px",
              fontSize: "13px",
              fontWeight: 700,
              background: tPrimary,
              color: "#fff",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: tPrimaryDk, boxShadow: "none" },
              "&.Mui-disabled": {
                opacity: 0.5,
                color: "#fff",
                background: tPrimary,
              },
            }}
          >
            {creating ? "Creating..." : "Create Semester"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
